import React from 'react';

import { Text } from '~/components/core';
import IconButton from '~/components/core/Atomic/Buttons/IconButton';
import ColoredDot from '~/components/core/Atomic/ColoredDot';
import type { DndDragHandleProps } from '~/components/core/DndOrderedList/DndOrderedList.types';
import { PencilIcon } from '~/components/icons';
import useOrganization from '~/components/OrganizationContext';
import OverflowTextWithToolTip from '~/components/OverflowTextWithToolTip';
import ReorderDragHandle from '~/components/SystemConfiguration/FnolConfiguration/FnolConfigurationScreen/components/ReorderDragHandle';
import Switch from '~/components/ui/atoms/Switch';

import type { ClaimFlagConfiguration } from '../../../../ClaimFlags/types';
import { useScopeSummary } from '../../ScopeFields/useScopeSummary';
import TEXTS from '../texts';

// Shared column template so the header row and every data row line up. Columns:
// drag handle · color · key · label · [sub-orgs] · lines of business · enabled · actions.
// The sub-orgs column only exists when the org has sub-organizations enabled (otherwise every flag
// is org-wide and the column would be meaningless), so the header and rows compute the grid together.
export const flagRowGrid = (showSubOrgs: boolean) =>
  showSubOrgs
    ? 'grid grid-cols-[32px_64px_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1.25fr)_minmax(0,1.25fr)_88px_72px] items-center gap-x-12'
    : 'grid grid-cols-[32px_64px_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1.25fr)_88px_72px] items-center gap-x-12';

type FlagConfigRowProps = {
  flag: ClaimFlagConfiguration;
  dragHandleProps: DndDragHandleProps;
  onToggle: () => void;
  onEdit: () => void;
  disabled: boolean;
};

const FlagConfigRow = ({ flag, dragHandleProps, onToggle, onEdit, disabled }: FlagConfigRowProps) => {
  const { key, label, color, is_enabled } = flag;
  const { subOrganizationEnabled } = useOrganization() as { subOrganizationEnabled?: boolean };
  const { subOrgSummary, lobSummary } = useScopeSummary(flag);

  return (
    <div className={`${flagRowGrid(!!subOrganizationEnabled)} rounded-lg bg-slate-200 px-8 py-12`}>
      <ReorderDragHandle dragHandleProps={dragHandleProps} disabled={disabled} iconSize={24} />
      <ColoredDot color={color} />
      <OverflowTextWithToolTip tooltipTitle={key}>
        <Text variant={Text.VARIANTS.SM} className="inline">
          {key}
        </Text>
      </OverflowTextWithToolTip>
      <OverflowTextWithToolTip tooltipTitle={label}>
        <Text variant={Text.VARIANTS.SM} weight="semi-bold" className="inline">
          {label}
        </Text>
      </OverflowTextWithToolTip>
      {subOrganizationEnabled && (
        <OverflowTextWithToolTip tooltipTitle={subOrgSummary}>
          <Text variant={Text.VARIANTS.SM} colorVariant={Text.COLOR_VARIANTS.SECONDARY} className="inline">
            {subOrgSummary}
          </Text>
        </OverflowTextWithToolTip>
      )}
      <OverflowTextWithToolTip tooltipTitle={lobSummary}>
        <Text variant={Text.VARIANTS.SM} colorVariant={Text.COLOR_VARIANTS.SECONDARY} className="inline">
          {lobSummary}
        </Text>
      </OverflowTextWithToolTip>
      <Switch
        checked={is_enabled}
        onChange={onToggle}
        disabled={disabled}
        aria-label={`${TEXTS.columns.enabled} — ${label}`}
        className="justify-self-end"
      />
      <IconButton onClick={onEdit} disabled={disabled} aria-label={TEXTS.editFlag} className="justify-self-end">
        <PencilIcon />
      </IconButton>
    </div>
  );
};

export default FlagConfigRow;
